import { useContext, useEffect, useRef, useState } from 'react';

import { AxiosError } from 'axios';

import { recomputeWorkerOnboardingRequest } from '../../../api/onboarding';
import { NotificationContext } from '../../../contexts/notificationContext';
import { useBooleanState } from '../../../hooks';
import { AnyOnboarding, getOnboardingModuleKeys } from '../../../utils/onboardingStatus';

const REFETCH_DELAY = 5000;

interface Props {
  // The company to recompute when the caller doesn't name one. pay-app has a selected
  // company; Legend has none and passes the company id on each call instead.
  companyId?: string;
  userId?: string;
  onboarding?: AnyOnboarding | null;
  refetch: () => Promise<void>;
}

// Triggers a worker-scoped onboarding recompute. The endpoint responds 202 Accepted:
// the work is asynchronous, so we re-read the status after a few seconds and keep the
// button disabled in the meantime. `triggered[].started: false` means a run is already
// in flight — that is still a success, never a retry. Only 503 is a real failure.
export const useOnboardingRecompute = ({ companyId, userId, onboarding, refetch }: Props) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const [recomputingCompanyId, setRecomputingCompanyId] = useState<string>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const recomputeOnboarding = async (targetCompanyId?: string) => {
    const company = targetCompanyId || companyId;
    if (!company || !userId || loading) {
      return;
    }
    setLoadingTrue();
    setRecomputingCompanyId(company);
    try {
      // Recompute only the modules we know are in play; an empty list means all three
      const modules = getOnboardingModuleKeys(onboarding, company);
      await recomputeWorkerOnboardingRequest(company, userId, modules);
      showNotification({
        message: 'Refreshing onboarding status — this can take a few seconds.',
        options: { variant: 'success' },
      });
    } catch (e) {
      if ((e as AxiosError)?.response?.status === 503) {
        showNotification({
          message: "Couldn't refresh the onboarding status right now. Please try again.",
          options: { variant: 'error' },
        });
      } else {
        handleServerError(e);
      }
    } finally {
      timeoutRef.current = setTimeout(async () => {
        await refetch();
        setLoadingFalse();
        setRecomputingCompanyId(undefined);
      }, REFETCH_DELAY);
    }
  };

  return { recomputeOnboarding, recomputeOnboardingLoading: loading, recomputingCompanyId };
};
